import React, { createContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { baseURL } from "../constants/url";
import axios from "axios";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartCount, setCartCount] = useState(0);

  // ✅ validate token before every API call
  const getValidToken = () => {
    const token = localStorage.getItem("authToken");
    if (!token) return null;

    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("user");
        return null;
      }
      return token;
    } catch {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      return null;
    }
  };

  // ✅ fetch cart items count
  const fetchCartCount = async () => {
    const token = getValidToken();
    if (!token) {
      setCartCount(0);
      return;
    }

    try {
      const response = await axios.get(`${baseURL}common/cart/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 🔑 Adjust based on API response structure
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (response.data?.results) {
        items = response.data.results;
      } else if (response.data?.cart) {
        items = response.data.cart;
      }

      setCartCount(items.length || 0);
    } catch (error) {
      console.error("❌ Failed to fetch cart count:", error);
      setCartCount(0);
    }
  };

  // ✅ add item to cart
  const addToCart = async (variantId, quantity = 1) => {
    const token = getValidToken();
    if (!token) {
      console.error("⚠️ User not logged in");
      return;
    }

    try {
      const response = await fetch(`${baseURL}common/cart/add/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ variant_id: variantId, quantity }),
      });

      const result = await response.json();

      if (response.ok) {
        console.log("✅ Added to cart:", result);
        fetchCartCount(); // 🔄 refresh after adding
      } else {
        console.error("❌ Error adding to cart:", result);
      }
    } catch (error) {
      console.error("❌ Failed to add to cart:", error);
    }
  };

  useEffect(() => {
    fetchCartCount();
  }, []);

  return (
    <CartContext.Provider value={{ cartCount, fetchCartCount, addToCart }}>
      {children}
    </CartContext.Provider>
  );
};
